import { Component, h, Prop, Element, State, Event, EventEmitter, Method, AttachInternals } from '@stencil/core';
import { formatFileSize } from '@utils/utils';

@Component({
  tag: 'spw-file-upload',
  styleUrl: 'spw-file-upload.scss',
  shadow: true,
  formAssociated: true,
})
export class SpwFileUpload {
  private inputElement: HTMLInputElement;

  @Element() el: HTMLSpwFileUploadElement;

  /** Indique si le champ est désactivé, rendant le téléversement de fichiers non disponible */
  @Prop() disabled: boolean = false;
  /** Indique s'il y a une erreur visuelle associée au composant de téléversement de fichiers */
  @Prop() error: boolean = false;
  /** Nom de l'élément utilisé dans les formulaires pour associer la valeur des fichiers */
  @Prop() name: string;
  /** Valeur actuelle sélectionnée, utilisée pour les formulaires */
  @Prop() value: string;
  /** Active ou désactive la zone de dépôt pour le glisser-déposer de fichiers */
  @Prop() enableDropZone: boolean = false;
  /** Permet de téléverser plusieurs fichiers si vrai */
  @Prop() multiple: boolean = false;
  /** Types de fichiers acceptés, spécifiés sous forme de chaîne de types MIME */
  @Prop() accept: string = '';
  /** Taille maximale autorisée pour chaque fichier, en Mo */
  @Prop() maxFileSize: number = 100;
  /** Nombre maximal de fichiers pouvant être téléversés */
  @Prop() maxFiles: number = Infinity;

  @State() internalValue: string;
  @State() selectedFiles: File[] = [];
  @State() fileTypeErrors: string[] = [];
  @State() fileLimitErrors: string[] = [];
  @State() isDragging: boolean = false;
  @State() isMaxFilesReachedExact: boolean = false;

  @AttachInternals() internals: ElementInternals;

  /** Événement émis lorsque la sélection de fichiers change */
  @Event({ bubbles: true, composed: true }) spwFileChange: EventEmitter<{ files: File[] }>;

  componentWillLoad() {
    this.internalValue = this.value;
    this.maxFileSize = this.maxFileSize * 1024 * 1024;
  }

  componentWillUpdate() {
    if (this.value !== this.internalValue) {
      this.internalValue = this.value;
    }
  }

  get isMaxFilesReached(): boolean {
    return this.selectedFiles.length >= this.maxFiles;
  }

  private handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this.processFiles(files);
  }

  private processFiles(files: File[]) {
    this.fileTypeErrors = [];
    this.fileLimitErrors = [];
    this.isMaxFilesReachedExact = false;

    if (!this.multiple) {
      this.selectedFiles = [];
      files = files.slice(0, 1);
    }

    const validFiles = files.filter(file => {
      const isFileAccepted = this.isFileAccepted(file);
      const isFileSizeValid = this.isFileSizeValid(file);

      if (!isFileAccepted && !isFileSizeValid) {
        this.fileTypeErrors.push(`${file.name} n’a pas été pris en compte car le format n’est pas autorisé et la limite de poids est dépassée.`);
        return false;
      } else if (!isFileAccepted) {
        this.fileTypeErrors.push(`${file.name} n'a pas été pris en compte car le format n'est pas autorisé.`);
        return false;
      } else if (!isFileSizeValid) {
        this.fileTypeErrors.push(`${file.name} n'a pas été pris en compte car il dépasse la taille maximale autorisée.`);
        return false;
      }

      return true;
    });

    if (this.selectedFiles.length + validFiles.length >= this.maxFiles) {
      const allowedFiles = this.maxFiles - this.selectedFiles.length;

      if (this.selectedFiles.length + validFiles.length === this.maxFiles) {
        this.isMaxFilesReachedExact = true;
        this.fileLimitErrors.push('Vous avez atteint la limite autorisée de fichiers.');
      } else {
        validFiles.splice(allowedFiles);
        const maxFilesText = this.maxFiles > 1 ? 'fichiers' : 'fichier';
        this.fileLimitErrors.push(`Vous ne pouvez pas télécharger plus de ${this.maxFiles} ${maxFilesText}.`);
      }
    }

    this.selectedFiles = [...this.selectedFiles, ...validFiles];

    this.spwFileChange.emit({ files: this.selectedFiles });
    this.updateFormValue();
  }

  private isFileAccepted(file: File): boolean {
    if (!this.accept) return true;
    const acceptedTypes = this.accept.split(',').map(type => type.trim());
    return acceptedTypes.includes(file.type) || acceptedTypes.some(type => file.name.endsWith(type));
  }

  private isFileSizeValid(file: File): boolean {
    return file.size <= this.maxFileSize;
  }

  private updateFormValue() {
    if (this.selectedFiles.length > 0) {
      const formData = new FormData();
      this.selectedFiles.forEach((file, index) => {
        formData.append(`${this.name}[${index}]`, file);
      });
      this.internalValue = formData.toString();
      this.internals.setFormValue(formData);
    } else {
      this.internalValue = '';
      this.internals.setFormValue(null);
    }
  }

  /** Réinitialise le champ de téléchargement de fichiers */
  @Method()
  async resetInput() {
    this.inputElement.value = '';
    this.selectedFiles = [];
    this.fileLimitErrors = [];
    this.fileTypeErrors = [];
    this.updateFormValue();
  }

  formResetCallback() {
    this.resetInput();
  }

  private handleDragOver(event: DragEvent) {
    event.preventDefault();
    if (!this.disabled && this.enableDropZone && !this.isMaxFilesReached) {
      this.isDragging = true;
    }
  }

  private handleDragLeave() {
    if (!this.disabled && this.enableDropZone) {
      this.isDragging = false;
    }
  }

  private handleDrop(event: DragEvent) {
    event.preventDefault();
    if (!this.disabled && this.enableDropZone && !this.isMaxFilesReached) {
      this.isDragging = false;
      const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
      this.processFiles(files);
    }
  }

  private removeFile(index: number) {
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
    this.fileLimitErrors = [];
    this.updateFormValue();
  }

  private handleKeydown(event) {
    if ((event.key === ' ' || event.key === 'Enter') && !this.isMaxFilesReached) {
      event.preventDefault();
      !this.disabled && this.inputElement.click();
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-file-upload__wrapper': true,
      'spw-file-upload__wrapper--disabled': this.disabled || this.isMaxFilesReached,
      'spw-file-upload__wrapper--error': this.error,
      'spw-file-upload--has-dropzone': this.enableDropZone,
      'spw-file-upload--is-dragging': this.isDragging,
    };
  }

  private get hintsMessages(): string[] {
    const maxSizeInMB = this.maxFileSize / (1024 * 1024);
    const acceptedFormats = this.accept ? this.accept : 'tous les formats';
    const messages = [];
    let message = `Taille maximale par fichier : ${maxSizeInMB} Mo`;

    if (this.multiple && this.maxFiles !== Infinity) {
      const maxFilesText = this.maxFiles > 1 ? 'fichiers' : 'fichier';
      message += ` - ${this.maxFiles} ${maxFilesText} maximum.`;
    } else {
      message += '.';
    }

    messages.push(message);
    messages.push(`Formats autorisés : ${acceptedFormats}.`);

    return messages;
  }

  render() {
    const inputTextNoDrop = this.multiple ? 'Choisir vos fichiers...' : 'Choisir votre fichier...';
    const inputTextDrop = this.multiple ? 'Déposez vos fichiers ici' : 'Déposez votre fichier ici';

    return (
      <div class={this.elementClass} onDragOver={this.handleDragOver.bind(this)} onDragLeave={this.handleDragLeave.bind(this)} onDrop={this.handleDrop.bind(this)}>
        <input
          type="file"
          ref={el => (this.inputElement = el)}
          onChange={this.handleFileChange.bind(this)}
          name={this.name}
          multiple={this.multiple}
          accept={this.accept}
          style={{ display: 'none' }}
          disabled={this.isMaxFilesReached}
        />
        {this.enableDropZone ? (
          <div
            tabindex={this.disabled || this.isMaxFilesReached ? -1 : 0}
            onKeyDown={this.handleKeydown.bind(this)}
            onClick={() => !this.disabled && !this.isMaxFilesReached && this.inputElement.click()}
            class="spw-file-upload__dropzone"
          >
            {inputTextDrop} ou <span class="spw-file-upload__dropzone-link">choisissez sur votre ordinateur...</span>
          </div>
        ) : (
          <spw-button variant="secondary" onClick={() => !this.disabled && !this.isMaxFilesReached && this.inputElement.click()} disabled={this.disabled || this.isMaxFilesReached}>
            {inputTextNoDrop}
          </spw-button>
        )}
        <div class="spw-file-upload__hints">
          {this.hintsMessages.length > 0 && (
            <ul>
              {this.hintsMessages.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          )}
        </div>
        <div class="spw-file-upload__info">
          {this.selectedFiles.length > 0 ? (
            <ul class="spw-file-upload__info-list">
              {this.selectedFiles.map((file, index) => (
                <li key={index}>
                  <span class="spw-file-upload__info-list-name">{file.name}</span>
                  <div class="spw-file-upload__info-list-actions">
                    <span class="spw-tw-mr-2">{formatFileSize(file.size)}</span>
                    <a
                      class="spw-file-upload__info-list-delete"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        this.removeFile(index);
                      }}
                    >
                      Supprimer
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            ''
          )}
          {this.fileLimitErrors.length > 0 && (
            <ul class={`spw-file-upload__errors ${this.isMaxFilesReachedExact ? 'spw-file-upload__errors--warning' : ''}`}>
              {this.fileLimitErrors.map((message, index) => (
                <li key={index}>
                  <spw-icon class="spw-tw-mr-2" icon={this.isMaxFilesReachedExact ? 'fa-circle-exclamation' : 'fa-warning'}></spw-icon>
                  {message}
                </li>
              ))}
            </ul>
          )}
          {this.fileTypeErrors.length > 0 && (
            <ul class="spw-file-upload__errors">
              {this.fileTypeErrors.map((message, index) => (
                <li key={index}>
                  <spw-icon class="spw-tw-mr-2" icon="fa-warning"></spw-icon>
                  {message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }
}
